import { forwardRef, ButtonHTMLAttributes } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'outline';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: string;
  iconRight?: string;
  fullWidth?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: `
    bg-primary-500 text-background-50
    hover:bg-primary-600 active:bg-primary-700
    disabled:opacity-50
    focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2
  `,
  secondary: `
    bg-accent-500 text-background-50
    hover:bg-accent-600 active:bg-accent-700
    disabled:opacity-50
    focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2
  `,
  outline: `
    bg-transparent border border-background-300 text-foreground-700
    hover:bg-background-100 hover:border-background-400 active:bg-background-200
    disabled:opacity-50
    focus-visible:ring-2 focus-visible:ring-foreground-300 focus-visible:ring-offset-2
  `,
  ghost: `
    bg-transparent text-foreground-700
    hover:bg-background-100 active:bg-background-200
    disabled:opacity-50
    focus-visible:ring-2 focus-visible:ring-foreground-300 focus-visible:ring-offset-2
  `,
  destructive: `
    bg-semantic-error text-background-50
    hover:opacity-90 active:opacity-80
    disabled:opacity-50
    focus-visible:ring-2 focus-visible:ring-semantic-error focus-visible:ring-offset-2
  `,
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2.5',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      iconLeft,
      iconRight,
      fullWidth = false,
      disabled,
      children,
      className = '',
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={`
          inline-flex items-center justify-center
          font-label font-medium
          rounded-lg
          whitespace-nowrap
          cursor-pointer
          transition-all duration-200
          select-none
          ${sizeClasses[size]}
          ${variantClasses[variant]}
          ${fullWidth ? 'w-full' : ''}
          ${isDisabled ? 'cursor-not-allowed' : ''}
          ${className}
        `}
        {...rest}
      >
        {loading ? (
          <i className="ri-loader-4-line animate-spin" />
        ) : iconLeft ? (
          <i className={iconLeft} />
        ) : null}
        {children}
        {!loading && iconRight && <i className={iconRight} />}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;